import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useMsal, useIsAuthenticated } from '@azure/msal-react';

import { userService } from '@/services/userService';

import type { CurrentUser } from '@/types/user';
import { getErrorMessage } from '@/helpers/errorHelper';

export type { CurrentUser };

interface AuthContextValue {
  user: CurrentUser | null;
  isLoading: boolean;
  error: string | null;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);

    userService.getCurrentUser()
      .then((data) => { if (!cancelled) setUser(data); })
      .catch((err: unknown) => { if (!cancelled) setError(getErrorMessage(err)); })
      .finally(() => { if (!cancelled) setIsLoading(false); });

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const logout = useCallback(() => {
    instance.logoutRedirect();
  }, [instance]);

  const value = useMemo(() => ({
    user,
    isLoading,
    error,
    logout,
  }), [user, isLoading, error, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
